import { useSpecificPaywallContext } from "app/paywalls/components/forms/editorV3/contexts/PaywallContext"
import { TElement } from "app/schema/paywallTemplate/element/element"
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTemplateJsonContext } from "../JsonProvider"
import { flattenArray } from "../JsonProvider/utils"
import { getElementById } from "./utils"

type TElementProvider = {
  children?: ReactNode
  selectedElement: TElement | null
  selectedElementId: string | null
  setSelectedElementId: Dispatch<SetStateAction<string | null>>
}

export const ElementContext = createContext<TElementProvider>({
  selectedElement: null,
  selectedElementId: null,
  setSelectedElementId: () => {},
})

export const ElementProvider = ({ children }) => {
  const { parsedJson } = useTemplateJsonContext()
  const { selectedSubstitutionKey } = useSpecificPaywallContext({
    selectedSubstitutionKey: true,
  })

  const [selectedElementId, setSelectedElementId] = useState<string | null>(null)

  const selectedElement = useMemo(() => {
    return getElementById(selectedElementId, parsedJson)
  }, [parsedJson, selectedElementId])

  const allComponents = useMemo(() => {
    const backgroundContentComponents = flattenArray(parsedJson?.backgroundContent || [])
    const mainContentComponents = flattenArray(parsedJson?.mainContent || [])
    const cardContentComponents = flattenArray(parsedJson?.cardContent || [])
    return [...backgroundContentComponents, ...mainContentComponents, ...cardContentComponents]
  }, [parsedJson])

  useEffect(() => {
    if (!selectedSubstitutionKey) {
      setSelectedElementId(null)
      return
    }

    const selectedComponent = allComponents.find((c) =>
      selectedSubstitutionKey.toLocaleLowerCase().includes(c.id.toLocaleLowerCase())
    )
    if (
      !selectedComponent &&
      selectedSubstitutionKey !== "navbar" &&
      selectedSubstitutionKey !== "purchase drawer" &&
      selectedSubstitutionKey !== "Purchase Drawer" &&
      selectedSubstitutionKey !== "footer"
    ) {
      setSelectedElementId(null)
    }
  }, [selectedSubstitutionKey, setSelectedElementId, allComponents])

  const value = useMemo(
    () => ({ selectedElement, selectedElementId, setSelectedElementId }),
    [selectedElement, selectedElementId, setSelectedElementId]
  )

  return <ElementContext.Provider value={value}>{children}</ElementContext.Provider>
}

export const useElementContext = () => useContext(ElementContext)
